<template>
  <div class="mobile-navigation d-lg-none" :class="{ 'is-open': rootStore.mobileMenuActive }">
    <button class="mobile-navigation__backdrop" type="button" aria-label="Close navigation" @click="toggleSideMenu"></button>
    <aside class="mobile-navigation__drawer">
      <div class="mobile-navigation__head" v-if="user">
        <div>
          <p class="mobile-navigation__eyebrow">Signed in as</p>
          <strong>{{ user.first_name || user.email }}</strong>
        </div>
        <button class="mobile-navigation__close" type="button" aria-label="Close navigation" @click="toggleSideMenu">×</button>
      </div>

      <div class="mobile-navigation__credits" v-if="user">
        <span>Credits</span>
        <strong>{{ creditsLabel }}</strong>
      </div>

      <Navigation />

      <div class="mobile-navigation__footer">
        <a class="mobile-navigation__button" :href="websiteUrl + 'subscription/?type=user#plan-wrapper'">Get credits</a>
        <button class="mobile-navigation__logout" type="button" @click="logout">Logout</button>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useFilterStore } from '@/stores/useFilterStore'
import { useRootStore } from '@/stores/useRootStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useRoute, useRouter } from 'vue-router'
import Navigation from '@/components/Layout/Navigation.vue'

defineProps<{ menu?: any; social: any[]; user: any }>()

const rootStore = useRootStore()
const filterStore = useFilterStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()
const websiteUrl = import.meta.env.VITE_WEBSITE_URL

const creditsLabel = computed(() => {
  if (!rootStore.user) return 'N/A'
  if (rootStore.hasUnlimitedSubscription) return 'Unlimited'
  return rootStore.user.credits
})

function toggleSideMenu() {
  rootStore.toggleMobileMenu()
  if (rootStore.mobileMenuActive) document.body.classList.add('mobile-nav-open')
  else document.body.classList.remove('mobile-nav-open')
}

function closeSideMenu() {
  if (!rootStore.mobileMenuActive) return
  rootStore.toggleMobileMenu()
  document.body.classList.remove('mobile-nav-open')
}

watch(
  () => `${route.fullPath}|${filterStore.mode}`,
  () => {
    closeSideMenu()
  }
)

async function logout() {
  ;(window as any).$chatwoot?.reset()
  await authStore.logout()
  closeSideMenu()
  router.push({ name: 'login' })
}
</script>

<style lang="scss">
@media screen and (max-width: 991px) {
  .mobile-navigation {
    position: fixed;
    inset: 0;
    z-index: 40;
    pointer-events: none;
    &.is-open {
      pointer-events: auto;
    }
    &__backdrop {
      position: absolute;
      inset: 0;
      border: 0;
      background: rgba(14, 15, 33, 0.42);
      opacity: 0;
      transition: opacity 0.22s ease;
    }
    &__drawer {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      width: min(88vw, 360px);
      padding: 18px 16px 22px;
      overflow-y: auto;
      background:
        radial-gradient(circle at top right, rgba(247, 153, 57, 0.2), transparent 24%),
        linear-gradient(180deg, #6d2d69 0%, #43224f 48%, #231733 100%);
      box-shadow: 24px 0 40px rgba(18, 10, 25, 0.28);
      transform: translateX(-100%);
      transition: transform 0.24s ease;
    }
    &__head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 16px;
      color: #fff;
      strong {
        display: block;
        font-size: 18px;
        line-height: 1.3;
      }
    }
    &__eyebrow {
      margin: 0 0 4px;
      font-size: 11px;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.6);
    }
    &__close {
      width: 40px;
      height: 40px;
      border: 0;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
      font-size: 28px;
      line-height: 1;
    }
    &__credits {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      margin-bottom: 18px;
      padding: 14px 16px;
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 18px;
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
      span {
        font-size: 12px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.62);
      }
      strong {
        font-size: 26px;
        font-family: "MuseoSans-700";
      }
    }
    &__footer {
      display: flex;
      flex-direction: column;
      gap: 10px;
      margin-top: 20px;
    }
    &__button,
    &__logout {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 48px;
      border-radius: 14px;
      text-decoration: none;
      font-family: "MuseoSans-500";
    }
    &__button {
      background: linear-gradient(135deg, #ffaa00, #f79939);
      color: #331f0a;
    }
    &__logout {
      border: 1px solid rgba(255, 255, 255, 0.16);
      background: transparent;
      color: #fff;
    }
    &.is-open &__backdrop {
      opacity: 1;
    }
    &.is-open &__drawer {
      transform: translateX(0);
    }
  }
  .mobile-nav-open {
    overflow: hidden;
  }
}
</style>
